import React, { createContext, useContext, useState, useCallback } from 'react';

const SETTINGS_KEY = 'themeGeneratorSettings';

/**
 * Read the persisted dark-mode preference from localStorage.
 * Returns false if nothing is stored or if parsing fails.
 */
function readDarkModeFromStorage() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed.darkMode === true;
  } catch {
    return false;
  }
}

const ThemeContext = createContext({
  darkMode: false,
  setDarkMode: () => {}
});

/**
 * ThemeContextProvider
 *
 * Wraps the application and exposes a `darkMode` boolean plus a `setDarkMode`
 * setter.  The setter both updates React state AND persists the preference to
 * localStorage so that Settings.js can use it directly.
 */
export const ThemeContextProvider = ({ children }) => {
  const [darkMode, setDarkModeState] = useState(readDarkModeFromStorage);

  const setDarkMode = useCallback((value) => {
    setDarkModeState(value);
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      const current = raw ? JSON.parse(raw) : {};
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ...current, darkMode: value }));
    } catch {
      // ignore storage errors
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);

export default ThemeContext;
